/**
 * Section Editor Modal
 *
 * Modal for editing individual contract template sections
 */

import React, { useState } from 'react';
import { LwModal } from '@/components/lw';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectLabel,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Plus, Trash2, ArrowUp, ArrowDown } from 'lucide-react';
import type { ContractSection, SectionContent, ContractVariableName, ContractVariableData } from './contract-types';
import { generateId, cloneTemplate, interpolateVariables } from './contract-utils';
import { SectionRenderer } from './ContractSectionRenderers';
import { CONTENT_LIBRARY } from './contract-content-library';

interface SectionEditorModalProps {
  section: ContractSection;
  variableData: ContractVariableData;
  onClose: () => void;
  onSave: (section: ContractSection) => void;
}

// Available variables for insertion
const AVAILABLE_VARIABLES: { value: ContractVariableName; label: string; example: string }[] = [
  { value: 'firstName', label: 'First Name', example: 'John' },
  { value: 'lastName', label: 'Last Name', example: 'Doe' },
  { value: 'fullName', label: 'Full Name', example: 'John Doe' },
  { value: 'email', label: 'Email', example: 'john.doe@example.com' },
  { value: 'phone', label: 'Phone', example: '(555) 123-4567' },
  { value: 'fullMailingAddress', label: 'Mailing Address', example: '123 Main St, Los Angeles, CA 90001' },
  { value: 'nmlsNumber', label: 'NMLS Number', example: '123456' },
  { value: 'dreNumber', label: 'DRE Number', example: 'DRE#01234567' },
  { value: 'bankedCompensation', label: 'Banked Compensation', example: '1.50%' },
  { value: 'brokerCommissionSplit', label: 'Broker Commission Split', example: '75%' },
  { value: 'bankedOverrideBps', label: 'Banked Override', example: '25 bps' },
  { value: 'position', label: 'Position', example: 'Loan Officer' },
  { value: 'startDate', label: 'Start Date', example: 'January 1, 2025' },
  { value: 'contractDate', label: 'Contract Date', example: 'January 1, 2025' },
  { value: 'companyName', label: 'Company Name', example: 'Lendwise Financial' }
];

// Standalone label styling (matches the kit `.field label` treatment).
const lbl: React.CSSProperties = { fontSize: 11.5, fontWeight: 600, color: 'var(--ink-soft)', display: 'block', marginBottom: 4, letterSpacing: '.01em' };
const mono: React.CSSProperties = { fontFamily: 'ui-monospace, monospace', fontSize: 13 };

export const SectionEditorModal: React.FC<SectionEditorModalProps> = ({
  section,
  variableData,
  onClose,
  onSave
}) => {
  const [workingSection, setWorkingSection] = useState<ContractSection>(cloneTemplate(section));

  // Interpolation function for preview using actual variable data
  const interpolate = (text: string) => {
    return interpolateVariables(text, variableData, { fallback: '[Not provided]' });
  };

  // Handle content item update
  const handleContentUpdate = (index: number, updates: Partial<SectionContent>) => {
    const updatedContent = [...workingSection.content];
    updatedContent[index] = { ...updatedContent[index], ...updates } as SectionContent;

    setWorkingSection({
      ...workingSection,
      content: updatedContent
    });
  };

  // Handle add content item
  const handleAddContent = (type: SectionContent['type']) => {
    let newContent: SectionContent;

    switch (type) {
      case 'heading':
        newContent = {
          id: generateId('content'),
          type: 'heading',
          text: 'New Heading',
          level: 2
        };
        break;
      case 'paragraph':
        newContent = {
          id: generateId('content'),
          type: 'paragraph',
          text: 'New paragraph text...'
        };
        break;
      case 'numbered_list':
        newContent = {
          id: generateId('content'),
          type: 'numbered_list',
          items: ['Item 1', 'Item 2']
        };
        break;
      default:
        return;
    }

    setWorkingSection({
      ...workingSection,
      content: [...workingSection.content, newContent]
    });
  };

  // Handle delete content item
  const handleDeleteContent = (index: number) => {
    // eslint-disable-next-line no-alert -- intentional native confirm
    if (!confirm('Delete this content item?')) {
      return;
    }

    const updatedContent = workingSection.content.filter((_, i) => i !== index);
    setWorkingSection({
      ...workingSection,
      content: updatedContent
    });
  };

  // Handle save
  const handleSave = () => {
    onSave(workingSection);
  };

  // Insert variable at cursor
  const insertVariable = (variable: ContractVariableName, targetIndex: number) => {
    const content = workingSection.content[targetIndex];
    if ('text' in content) {
      const updatedText = `${content.text} {{${variable}}}`;
      handleContentUpdate(targetIndex, { text: updatedText });
    }
  };

  // Move content item up or down within section
  const handleMoveContent = (index: number, direction: 'up' | 'down') => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    // Check boundaries
    if (targetIndex < 0 || targetIndex >= workingSection.content.length) {
      return;
    }

    // Swap content items
    const newContent = [...workingSection.content];
    [newContent[index], newContent[targetIndex]] = [newContent[targetIndex], newContent[index]];

    setWorkingSection({
      ...workingSection,
      content: newContent
    });
  };

  // Add optional block from library
  const handleAddOptionalBlock = (blockId: string) => {
    // Get the library for this section type
    const library = CONTENT_LIBRARY[workingSection.type];
    if (!library) {
      console.error('No content library for section:', workingSection.type);
      return;
    }

    // Find the specific block
    const block = library.optionalBlocks.find(b => b.id === blockId);
    if (!block) {
      console.error('Block not found:', blockId);
      return;
    }

    // Clone the default content and assign new IDs
    const newContent: SectionContent[] = block.defaultContent.map(item => ({
      ...item,
      id: generateId('content'),
      isOptional: true,
      optionalBlockId: blockId
    }));

    // Add to section content
    setWorkingSection({
      ...workingSection,
      content: [...workingSection.content, ...newContent]
    });
  };

  // Remove optional block
  const handleRemoveOptionalBlock = (index: number) => {
    const content = workingSection.content[index];

    if (!content.isOptional) {
      // eslint-disable-next-line no-alert -- intentional native guardrail
      alert('Cannot remove required content');
      return;
    }

    // Get the block label for confirmation
    const library = CONTENT_LIBRARY[workingSection.type];
    const block = library?.optionalBlocks.find(b => b.id === content.optionalBlockId);
    const blockName = block?.label || 'this content block';

    // eslint-disable-next-line no-alert -- intentional native confirm
    if (!confirm(`Remove "${blockName}"? This will delete all related content.`)) {
      return;
    }

    // Remove all content with the same optionalBlockId
    const newContent = workingSection.content.filter(
      item => item.optionalBlockId !== content.optionalBlockId
    );

    setWorkingSection({
      ...workingSection,
      content: newContent
    });
  };

  return (
    <LwModal
      size="tall"
      eyebrow="Contract"
      title={`Edit Section: ${section.title}`}
      sub="Customize the content for this contract section"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-sm" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn-sm solid" onClick={handleSave}>
            Save Section
          </button>
        </>
      }
    >
      {/* Split View Container */}
      <div style={{ display: 'flex', gap: 16, flex: 1, minHeight: 0 }}>
        {/* Left Column: Edit Content */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <div className="dw-sec-title">Edit Content</div>
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingRight: 12, display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* Section Info */}
            <div className="panel" style={{ padding: 14 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <label style={lbl}>Section Type</label>
                  <p style={{ fontSize: 13, color: 'var(--lw-muted)', margin: 0 }}>{section.type}</p>
                </div>
                <div>
                  <label style={lbl}>Order</label>
                  <p style={{ fontSize: 13, color: 'var(--lw-muted)', margin: 0 }}>{section.order}</p>
                </div>
              </div>
            </div>

            {/* Content Items */}
            {workingSection.content.map((content, index) => (
              <div
                key={content.id}
                className="panel"
                style={
                  content.isOptional
                    ? { padding: 14, background: 'var(--gold-wash)', boxShadow: 'inset 0 0 0 1px rgba(190, 142, 62, .3)' }
                    : { padding: 14 }
                }
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, textTransform: 'capitalize' }}>
                      {content.type.replace(/_/g, ' ')}
                    </span>
                    {content.isOptional && (
                      <span className="badge" style={{ background: '#EEEADD', color: '#6F6A57', height: 21, fontSize: 11 }}>
                        Optional Block
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 2 }}>
                    {/* Move Up Button */}
                    <button
                      type="button"
                      className="iconbtn"
                      onClick={() => handleMoveContent(index, 'up')}
                      disabled={index === 0}
                      style={index === 0 ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
                    >
                      <ArrowUp />
                    </button>

                    {/* Move Down Button */}
                    <button
                      type="button"
                      className="iconbtn"
                      onClick={() => handleMoveContent(index, 'down')}
                      disabled={index === workingSection.content.length - 1}
                      style={index === workingSection.content.length - 1 ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
                    >
                      <ArrowDown />
                    </button>

                    {/* Delete Button - different handler for optional blocks */}
                    <button
                      type="button"
                      className="iconbtn"
                      onClick={() => content.isOptional ? handleRemoveOptionalBlock(index) : handleDeleteContent(index)}
                      title={content.isOptional ? 'Remove this optional block' : 'Delete this content item'}
                    >
                      <Trash2 style={{ color: content.isOptional ? 'var(--gold-600)' : 'var(--danger)' }} />
                    </button>
                  </div>
                </div>

                {/* Heading Editor */}
                {content.type === 'heading' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div className="field">
                      <label htmlFor={`text-${index}`}>Heading Text</label>
                      <input
                        id={`text-${index}`}
                        value={content.text}
                        onChange={(e) => handleContentUpdate(index, { text: e.target.value })}
                      />
                    </div>
                    <div className="field">
                      <label htmlFor={`level-${index}`}>Level</label>
                      <Select
                        value={content.level.toString()}
                        onValueChange={(value) =>
                          handleContentUpdate(index, { level: parseInt(value) as 1 | 2 | 3 | 4 })
                        }
                      >
                        <SelectTrigger id={`level-${index}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="lw-ds">
                          <SelectItem value="1">H1 - Main Title</SelectItem>
                          <SelectItem value="2">H2 - Section</SelectItem>
                          <SelectItem value="3">H3 - Subsection</SelectItem>
                          <SelectItem value="4">H4 - Minor Heading</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                {/* Paragraph Editor */}
                {content.type === 'paragraph' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div className="field">
                      <label htmlFor={`text-${index}`}>Paragraph Text</label>
                      <textarea
                        id={`text-${index}`}
                        value={content.text}
                        onChange={(e) => handleContentUpdate(index, { text: e.target.value })}
                        rows={4}
                        style={mono}
                      />
                    </div>
                    <div className="field">
                      <label>Insert Variable</label>
                      <Select onValueChange={(value) => insertVariable(value as ContractVariableName, index)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select variable to insert..." />
                        </SelectTrigger>
                        <SelectContent className="lw-ds">
                          {AVAILABLE_VARIABLES.map((variable) => (
                            <SelectItem key={variable.value} value={variable.value}>
                              {variable.label} - {`{{${variable.value}}}`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                {/* List Editor */}
                {(content.type === 'numbered_list' || content.type === 'bulleted_list') && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <label style={lbl}>List Items</label>
                    {content.items.map((item, itemIndex) => (
                      <div key={itemIndex} style={{ display: 'flex', gap: 8 }}>
                        <div className="field" style={{ flex: 1 }}>
                          <textarea
                            value={item}
                            onChange={(e) => {
                              const newItems = [...content.items];
                              newItems[itemIndex] = e.target.value;
                              handleContentUpdate(index, { items: newItems });
                            }}
                            rows={2}
                            style={mono}
                          />
                        </div>
                        <button
                          type="button"
                          className="iconbtn"
                          onClick={() => {
                            const newItems = content.items.filter((_, i) => i !== itemIndex);
                            handleContentUpdate(index, { items: newItems });
                          }}
                        >
                          <Trash2 />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="btn-sm"
                      style={{ alignSelf: 'flex-start' }}
                      onClick={() => {
                        const newItems = [...content.items, 'New item'];
                        handleContentUpdate(index, { items: newItems });
                      }}
                    >
                      <Plus />
                      Add Item
                    </button>
                  </div>
                )}

                {/* Table Editor */}
                {content.type === 'table' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {/* Header Toggle */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input
                        type="checkbox"
                        id={`headers-${index}`}
                        checked={!!content.headers}
                        onChange={(e) => {
                          if (e.target.checked) {
                            const colCount = content.rows[0]?.length || 2;
                            handleContentUpdate(index, {
                              headers: Array(colCount).fill('Column')
                            });
                          } else {
                            handleContentUpdate(index, { headers: undefined });
                          }
                        }}
                      />
                      <label htmlFor={`headers-${index}`} style={{ ...lbl, marginBottom: 0 }}>Show Headers</label>
                    </div>

                    {/* Header Row Editor */}
                    {content.headers && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <label style={lbl}>Column Headers</label>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {content.headers.map((header, colIndex) => (
                            <div key={colIndex} className="field" style={{ flexDirection: 'row', gap: 4, width: 'auto' }}>
                              <input
                                value={header}
                                onChange={(e) => {
                                  const newHeaders = [...content.headers!];
                                  newHeaders[colIndex] = e.target.value;
                                  handleContentUpdate(index, { headers: newHeaders });
                                }}
                                style={{ width: 128 }}
                                placeholder={`Column ${colIndex + 1}`}
                              />
                              {content.headers!.length > 1 && (
                                <button
                                  type="button"
                                  className="iconbtn"
                                  onClick={() => {
                                    const newHeaders = content.headers!.filter((_, i) => i !== colIndex);
                                    const newRows = content.rows.map(row =>
                                      row.filter((_, i) => i !== colIndex)
                                    );
                                    handleContentUpdate(index, {
                                      headers: newHeaders,
                                      rows: newRows
                                    });
                                  }}
                                >
                                  <Trash2 />
                                </button>
                              )}
                            </div>
                          ))}
                          <button
                            type="button"
                            className="btn-sm"
                            onClick={() => {
                              const newHeaders = [...(content.headers || []), 'New Column'];
                              const newRows = content.rows.map(row => [...row, '']);
                              handleContentUpdate(index, {
                                headers: newHeaders,
                                rows: newRows
                              });
                            }}
                          >
                            <Plus />
                            Add Column
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Table Rows Editor */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      <label style={lbl}>Table Rows</label>
                      {content.rows.map((row, rowIndex) => (
                        <div key={rowIndex} className="panel" style={{ padding: 12, background: 'var(--cream-2)' }}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8 }}>
                            <label style={{ fontSize: 11, color: 'var(--lw-muted)' }}>Row {rowIndex + 1}</label>
                            <button
                              type="button"
                              className="iconbtn"
                              onClick={() => {
                                const newRows = content.rows.filter((_, i) => i !== rowIndex);
                                handleContentUpdate(index, { rows: newRows });
                              }}
                            >
                              <Trash2 style={{ color: 'var(--danger)' }} />
                            </button>
                          </div>

                          <div style={{ display: 'grid', gap: 8, gridTemplateColumns: `repeat(${row.length}, 1fr)` }}>
                            {row.map((cell, cellIndex) => (
                              <div key={cellIndex} className="field" style={{ gap: 4 }}>
                                <input
                                  value={cell}
                                  onChange={(e) => {
                                    const newRows = [...content.rows];
                                    newRows[rowIndex] = [...newRows[rowIndex]];
                                    newRows[rowIndex][cellIndex] = e.target.value;
                                    handleContentUpdate(index, { rows: newRows });
                                  }}
                                  placeholder={content.headers?.[cellIndex] || `Cell ${cellIndex + 1}`}
                                  style={{ ...mono, fontSize: 12 }}
                                />
                                <Select
                                  onValueChange={(value) => {
                                    const newRows = [...content.rows];
                                    newRows[rowIndex] = [...newRows[rowIndex]];
                                    newRows[rowIndex][cellIndex] += ` {{${value}}}`;
                                    handleContentUpdate(index, { rows: newRows });
                                  }}
                                >
                                  <SelectTrigger style={{ height: 28, fontSize: 12 }}>
                                    <SelectValue placeholder="+ Variable" />
                                  </SelectTrigger>
                                  <SelectContent className="lw-ds">
                                    {AVAILABLE_VARIABLES.map((variable) => (
                                      <SelectItem key={variable.value} value={variable.value} style={{ fontSize: 12 }}>
                                        {variable.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}

                      <button
                        type="button"
                        className="btn-sm"
                        style={{ alignSelf: 'flex-start' }}
                        onClick={() => {
                          const colCount = content.rows[0]?.length || (content.headers?.length || 2);
                          const newRow = Array(colCount).fill('');
                          handleContentUpdate(index, {
                            rows: [...content.rows, newRow]
                          });
                        }}
                      >
                        <Plus />
                        Add Row
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {/* Insert Content Block from Library */}
            {CONTENT_LIBRARY[workingSection.type] && CONTENT_LIBRARY[workingSection.type]!.optionalBlocks.length > 0 && (
              <div style={{ borderTop: '1px solid var(--line-soft)', paddingTop: 16, marginTop: 4 }}>
                <label style={{ ...lbl, fontSize: 13, marginBottom: 8 }}>Insert Content Block</label>
                <Select onValueChange={(blockId) => handleAddOptionalBlock(blockId)} value="">
                  <SelectTrigger style={{ width: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', fontSize: 13 }}>
                      <Plus size={15} style={{ marginRight: 8 }} />
                      <span>Choose a block to add...</span>
                    </div>
                  </SelectTrigger>
                  <SelectContent className="lw-ds">
                    {(() => {
                      const library = CONTENT_LIBRARY[workingSection.type]!;
                      const blocksByCategory = library.optionalBlocks.reduce((acc, block) => {
                        if (!acc[block.category]) {
                          acc[block.category] = [];
                        }
                        acc[block.category].push(block);
                        return acc;
                      }, {} as Record<string, typeof library.optionalBlocks>);

                      return Object.entries(blocksByCategory).map(([category, blocks]) => (
                        <SelectGroup key={category}>
                          <SelectLabel>{category}</SelectLabel>
                          {blocks.map(block => (
                            <SelectItem key={block.id} value={block.id}>
                              <div style={{ padding: '4px 0' }}>
                                <div style={{ fontWeight: 500, fontSize: 13 }}>{block.label}</div>
                                <div style={{ fontSize: 11, color: 'var(--lw-muted)', marginTop: 2 }}>{block.description}</div>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      ));
                    })()}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Add Content Button */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
              {!workingSection.content.some(c => c.type === 'heading') && (
                <button type="button" className="btn-sm" onClick={() => handleAddContent('heading')}>
                  <Plus />
                  Heading
                </button>
              )}
              <button type="button" className="btn-sm" onClick={() => handleAddContent('paragraph')}>
                <Plus />
                Paragraph
              </button>
              {!workingSection.content.some(c => c.type === 'numbered_list' || c.type === 'bulleted_list') && (
                <button type="button" className="btn-sm" onClick={() => handleAddContent('numbered_list')}>
                  <Plus />
                  Numbered List
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Right Column: Live Preview */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, borderLeft: '1px solid var(--line-soft)', paddingLeft: 16 }}>
          <div className="dw-sec-title">Live Preview</div>
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            <div className="panel" style={{ padding: 24 }}>
              <SectionRenderer
                section={{
                  id: workingSection.id,
                  title: workingSection.title,
                  content: workingSection.content
                }}
                variableData={variableData}
                checkboxStates={{}}
                onCheckboxChange={() => {}}
                readOnly={true}
                interpolate={interpolate}
              />
            </div>
          </div>
        </div>
      </div>
    </LwModal>
  );
};
